import {decode} from 'he'
import {http} from '../http'
import * as coreAlbums from '../../coreApi/albums'
import {APIResponse} from '../types'
import {ResourceID, Album} from '../../../types'
import {handleError} from '../handleError'
import {translateAlbumSortType} from './utils/translateAlbumSortType'

interface Params extends Omit<Album, 'photoCount' | 'constraints'> {
  clientID: ResourceID
}

type ResolvedReturn = APIResponse<Album>

export interface UpdateAlbum {
  (params: Params): Promise<ResolvedReturn>
}

const ERROR_PATH = 'api.albums.updateAlbum()'
const GENERIC_ERROR =
  'Something went wrong while updating your album. Please try again.'

export const updateAlbum: UpdateAlbum = async ({
  ID,
  clientID,
  name,
  date,
  season,
  isPrivate,
  coverPhotoID,
  sortType,
  isDraft,
  publishDate,
}) => {
  let resolvedReturn: ResolvedReturn

  try {
    const rawParamMap = {
      ID: parseInt(ID, 10),
      ClientID: parseInt(clientID, 10),
      Name: name,
      Date: date.toISOString(),
      Season: season,
      IsPrivate: isPrivate,
      CoverPhotoID: parseInt(coverPhotoID ?? '-1', 10),
      SortTypeID: translateAlbumSortType(sortType),
      IsDraft: isDraft,
      PublishDate: publishDate ? publishDate.toISOString() : null,
    }

    const response = await coreAlbums.update(http, rawParamMap)

    if (!response || !response.data) {
      throw new Error('Unsuccessful Album Update')
    }

    resolvedReturn = {
      result: 'success',
      data: {
        ID: response.data.ID.toString(),
        name: decode(response.data.Name),
        coverPhotoID: response.data.CoverPhotoID
          ? response.data.CoverPhotoID.toString()
          : null,
        date: new Date(response.data.Date),
        photoCount: response.data.PhotoCount,
        isPrivate: response.data.IsPrivate,
        season: response.data.Season,
        sortType: translateAlbumSortType(response.data.SortTypeID),
        isDraft: response.data.IsDraft,
        publishDate: response.data.PublishDate
          ? new Date(response.data.PublishDate)
          : null,
        constraints: (response.data.Constraints || []).map((constraint) => ({
          type: constraint.Type,
          constraintID: constraint.ConstraintID.toString(),
        })),
      },
    }
  } catch (e) {
    resolvedReturn = handleError(ERROR_PATH, GENERIC_ERROR, e)
  }

  return Promise.resolve(resolvedReturn)
}
